import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, Badge, Button } from '../components/Shared';
import { faqs } from '../data/mock';
import SafeIcon from '../common/SafeIcon';
import { FiChevronDown, FiChevronUp, FiCheck, FiPlay, FiTarget, FiBox, FiShield, FiTrendingUp, FiActivity } from 'react-icons/fi';

const Guide = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [openFaq, setOpenFaq] = useState(null);
  const [demoStep, setDemoStep] = useState(0);

  const demoSteps = [
    { title: "Intake", desc: "Upload vendor contracts or import via ERP.", icon: FiBox },
    { title: "Compliance", desc: "Automated SOC2 and W-9 verification.", icon: FiShield },
    { title: "Risk Mapping", desc: "Visualize strategic value vs risk on the Radar.", icon: FiTarget },
    { title: "Renewal Tracking", desc: "Contracts sorted into automated urgency lanes.", icon: FiActivity },
    { title: "Performance", desc: "Quarterly reviews and automated SLA tracking.", icon: FiTrendingUp },
    { title: "Optimization", desc: "Identify redundant spend across categories.", icon: FiCheck }
  ];

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-10 pb-20">
      <div className="text-center space-y-4">
        <Badge type="warning">Product Documentation</Badge>
        <h1 className="text-4xl font-extrabold text-white">Welcome to VendorVista</h1>
        <p className="text-slate-400 max-w-2xl mx-auto">
          The ultimate control center for procurement and operations teams to manage risk, spend, and vendor relationships in a unified intelligence platform.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex justify-center border-b border-slate-800">
        {['overview', 'demo', 'faq'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-8 py-4 text-sm font-bold uppercase tracking-widest transition-all relative ${
              activeTab === tab ? 'text-yellow-400' : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            {tab}
            {activeTab === tab && (
              <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-400" />
            )}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div 
            key="overview"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="p-8 border-l-4 border-yellow-400">
                <h3 className="text-xl font-bold text-white mb-4">What it does</h3>
                <p className="text-slate-400 leading-relaxed">
                  VendorVista solves the "fragmented vendor data" problem. Instead of tracking contracts in spreadsheets and risk in emails, we provide a **single source of truth**. 
                  <br/><br/>
                  We aggregate contract timelines, security compliance, and financial spend into a futuristic dashboard designed for high-velocity teams.
                </p>
              </Card>
              <Card className="p-8 border-l-4 border-blue-400">
                <h3 className="text-xl font-bold text-white mb-4">Key Outcomes</h3>
                <ul className="space-y-3 text-slate-400">
                  <li className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-emerald-400" /> Eliminate auto-renewal surprises</li>
                  <li className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-emerald-400" /> Reduce vendor risk by 40%</li>
                  <li className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-emerald-400" /> Standardize onboarding workflows</li>
                  <li className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-emerald-400" /> Real-time spend visibility</li>
                </ul>
              </Card>
            </div>
          </motion.div>
        )}

        {activeTab === 'demo' && (
          <motion.div 
            key="demo"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="bg-[#0A0F1C] border border-slate-800 rounded-2xl p-8">
              <div className="flex justify-between items-center mb-10">
                <div>
                  <h3 className="text-2xl font-bold text-white">How It Works</h3>
                  <p className="text-slate-400">Interactive workflow simulator</p>
                </div>
                <Badge type="warning">Step {demoStep + 1} of {demoSteps.length}</Badge>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                <div className="space-y-4">
                  {demoSteps.map((step, i) => (
                    <button
                      key={i}
                      onClick={() => setDemoStep(i)}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
                        demoStep === i 
                          ? 'bg-yellow-400/10 border-yellow-400 text-yellow-400' 
                          : 'bg-slate-900/50 border-slate-800 text-slate-500 hover:border-slate-600'
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${demoStep === i ? 'bg-yellow-400 text-black' : 'bg-slate-800'}`}>
                        <SafeIcon icon={step.icon} />
                      </div>
                      <span className="font-bold">{step.title}</span>
                    </button>
                  ))}
                </div>

                <div className="lg:col-span-2">
                  <Card className="p-10 bg-slate-900/50 border-dashed border-2 border-slate-700 min-h-[300px] flex flex-col justify-center items-center text-center">
                    <motion.div 
                      key={demoStep}
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="space-y-6"
                    >
                      <div className="w-20 h-20 bg-yellow-400/20 rounded-full flex items-center justify-center mx-auto">
                        <SafeIcon icon={demoSteps[demoStep].icon} className="text-4xl text-yellow-400" />
                      </div>
                      <h4 className="text-2xl font-bold text-white">{demoSteps[demoStep].title}</h4>
                      <p className="text-slate-400 max-w-md mx-auto text-lg leading-relaxed">
                        {demoSteps[demoStep].desc}
                      </p>
                      <div className="pt-4">
                        <Button 
                          onClick={() => setDemoStep((demoStep + 1) % demoSteps.length)}
                          className="flex items-center gap-2"
                        >
                          {demoStep === demoSteps.length - 1 ? "Restart Flow" : "Next Step"} <SafeIcon icon={FiPlay} />
                        </Button>
                      </div>
                    </motion.div>
                  </Card>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'faq' && (
          <motion.div 
            key="faq"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {faqs.map((faq, i) => (
              <Card key={i} className="overflow-hidden">
                <button 
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full p-6 flex justify-between items-center text-left hover:bg-slate-800/20 transition-colors"
                >
                  <span className="font-bold text-white">{faq.q}</span>
                  <SafeIcon icon={openFaq === i ? FiChevronUp : FiChevronDown} className="text-yellow-400" />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div 
                      initial={{ height: 0 }}
                      animate={{ height: 'auto' }}
                      exit={{ height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 text-slate-400 leading-relaxed border-t border-slate-800 pt-4">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="text-center pt-10 border-t border-slate-800">
        <p className="text-slate-500 text-sm">Made by LingLink AI and DigLab AI</p>
      </div>
    </div>
  );
};

export default Guide;